/**
 * English string table — the single source of truth for all UI copy.
 * Keys follow the pattern  <scope>.<name>  (dot-separated, camelCase).
 *
 * "as const" makes every value a string literal so StringKey is exact.
 */
export const en = {
  /* ---- Navigation ---- */
  "nav.library":        "Library",
  "nav.projects":       "Projects",
  "nav.help":           "Help",
  "nav.admin":          "Admin",
  "nav.newTranscript":  "New Transcript",
  "nav.signOut":        "Sign Out",
  "nav.addProject":     "Add project",
  "nav.unassigned":     "Unassigned",

  /* ---- Library page ---- */
  "library.searchPlaceholder":  "Search transcripts…",
  "library.filterAll":          "All",
  "library.filterReady":        "Ready",
  "library.filterInProgress":   "In progress",
  "library.filterArchived":     "Archived",
  "library.sortNewest":         "Newest first",
  "library.sortOldest":         "Oldest first",
  "library.emptyTitle":         "No transcripts yet",
  "library.emptyDesc":          "Upload a recording to get started.",
  "library.archivedBadge":      "Archived",
  "library.heading":            "Transcripts",
  "library.headingArchived":    "Archived transcripts",
  "library.newTranscript":      "New transcript",
  "library.archived":           "Archived",
  "library.backToLibrary":      "Back to transcripts",
  "library.sortLabel":          "Sort",
  "library.projectLabel":       "Project",
  "library.lockLabel":          "Lock",
  "library.allProjects":        "All projects",
  "library.unassigned":         "Unassigned",
  "library.allLock":            "All",
  "library.lockedOnly":         "Locked only",
  "library.unlockedOnly":       "Unlocked only",
  "library.noMatches":          "No transcripts match the current filters.",
  "library.noArchived":         "No archived transcripts.",
  "library.rename":             "Rename",
  "library.reassignProject":    "Reassign project",
  "library.archive":            "Archive",
  "library.restore":            "Restore",
  "library.deletePermanently":  "Delete permanently",
  "library.sortCreatedDesc":    "Created (newest first)",
  "library.sortCreatedAsc":     "Created (oldest first)",
  "library.sortTitleAsc":       "Title (A → Z)",
  "library.sortTitleDesc":      "Title (Z → A)",
  "library.sortDurationDesc":   "Duration (longest first)",
  "library.sortDurationAsc":    "Duration (shortest first)",
  "library.sortLockedFirst":    "Locked first",
  "library.sortUnlockedFirst":  "Unlocked first",
  "library.transcriptsCount":   "{n} transcripts",
  "library.transcriptCountOne": "1 transcript",

  /* ---- Status labels (stage vocabulary — spec §9) ---- */
  "status.ready":                 "Your review",
  "status.transcribing":          "Transcription",
  "status.annotating":            "Analysis",
  "status.pending":               "Briefing",
  "status.uploaded":              "Briefing",
  "status.error":                 "Error",
  "status.awaitingContextReview": "Briefing — needs you",
  "status.final":                 "Final",

  /* ---- Upload / new transcript ---- */
  "upload.dropzoneTitle":       "Drop your recording here",
  "upload.dropzoneSubtitle":    "or click to browse · audio or video",
  "upload.dropzoneInvalid":     "Only audio or video files are supported.",
  "upload.contextLabel":        "Meeting context",
  "upload.contextHelper":
    "This is the main accuracy lever — tell us who was in the meeting and what it was about.",
  "upload.contextPlaceholder":  "e.g. Product sync with Ilan and Roni. Topics: roadmap Q3, launch risks…",
  "upload.attendeesLabel":      "Attendees",
  "upload.projectLabel":        "Project",
  "upload.languageLabel":       "Primary language",
  "upload.titleLabel":          "Title",
  "upload.titlePlaceholder":    "e.g. G Electronics weekly sync",
  "upload.recordedAtLabel":     "When was this recorded?",
  "upload.recordedAtHelper":    "Used to show real-time timestamps in the transcript.",
  "upload.reviewContextLabel":  "Review parsed context before transcribing",
  "upload.reviewContextHelper": "Pause after we parse your notes, so you can fix name spellings or remove the wrong attendees before the recording is sent for transcription.",
  "upload.clearFile":           "Clear file selection",
  "upload.submitIdle":          "Upload + transcribe",
  "upload.submitCreating":      "Creating record…",
  "upload.submitDispatching":   "Dispatching to worker…",
  "upload.submitDone":          "Done",
  "upload.projectHelper":       "This meeting will use attendees + glossary from \"{name}\" + your workspace-wide registry.",
  "upload.projectHelperNone":   "This meeting will use only your workspace-wide attendees + glossary.",
  "upload.submitButton":        "Start transcription",
  "upload.uploading":           "Uploading…",
  "upload.processing":          "Processing…",
  "upload.pageTitle":           "New transcript",

  /* ---- Meeting page ---- */
  "player.following":     "Following",
  "player.follow":        "Follow",
  "meeting.lock":         "Lock",
  "meeting.unlock":       "Unlock",
  "meeting.updateAnalysis": "Update the analysis",
  "meeting.exportMd":     "Export Markdown",
  "meeting.exportJson":   "Export JSON",
  "meeting.exportDocx":   "Export Word",
  "meeting.exportAudio":  "Export Audio",
  "meeting.tabSummary":   "Summary",
  "meeting.tabTranscript":"Transcript",
  "meeting.reviewRail":   "Review",
  "meeting.contextCard":       "What the transcriber was told",
  "meeting.contextDisclosure": "What the transcriber was told",
  "meeting.contextBriefing":   "The briefing built from your notes",
  "meeting.noProposals":       "No new names or terms were proposed for this meeting. Update the analysis to scan the transcript again.",
  "meeting.dangerZone":        "Danger zone",
  "meeting.deleteConfirm":"Delete this transcript permanently?",
  "meeting.archiveLabel": "Archive transcript",
  "meeting.deleteLabel":  "Delete permanently",
  "meeting.exportButton": "Export",
  "meeting.preparing":    "Preparing…",

  /* ---- Summary tab ---- */
  "summary.aiLabel":      "AI summary — based on the transcript",
  "summary.overview":     "Overview",
  "summary.keyMoments":   "Key moments",
  "summary.actionItems":  "Action items",
  "summary.decisions":    "Decisions",
  "summary.emptyTitle":   "No summary yet",
  "summary.emptyDesc":    "The summary will appear once transcription and analysis complete.",
  "summary.emptyBody":    "No summary yet. Generate one — it re-reads the transcript only, nothing else changes.",
  "summary.regenerate":   "Regenerate the summary",
  "summary.generate":     "Generate the summary",
  "summary.regenerating": "Regenerating…",

  /* ---- Login ---- */
  "login.heading":              "Welcome to Timlul",
  "login.subheading":           "Sign in with your work email to continue.",
  "login.signIn":               "Sign in",
  "login.tagline":              "Hebrew + English meeting transcription. Enter your email to receive a sign-in link.",
  "login.emailLabel":           "Email address",
  "login.emailPlaceholder":     "you@example.com",
  "login.sendLink":             "Send magic link",
  "login.sending":              "Sending…",
  "login.checkInbox":           "Check your inbox",
  "login.checkInboxDetail":     "We sent a sign-in link to",
  "login.checkEmail":           "Check your email for a sign-in link.",
  "login.inviteOnly":           "This workspace is invite-only.",
  "login.inviteNeededEn":       "This email isn't on Timlul yet. Access is by invitation — please ask an administrator to invite you.",
  "login.inviteNeededHe":       "האימייל הזה עדיין לא רשום בתמלול. הגישה היא בהזמנה בלבד — אנא בקשו ממנהל המערכת להזמין אתכם.",

  /* ---- Projects ---- */
  "projects.heading":          "Projects",
  "projects.headingArchived":  "Archived projects",
  "projects.addProject":       "Add project",
  "projects.archived":         "Archived",
  "projects.backToProjects":   "Back to projects",
  "projects.emptyTitle":       "No projects yet",
  "projects.emptyDesc":        "A project groups attendees, glossary terms, and transcripts so context stays relevant per client or topic.",
  "projects.emptyArchivedTitle": "No archived projects",
  "projects.emptyArchivedDesc":  "Archived projects appear here. You can restore them at any time.",
  "projects.contextHelper":    "Flows into the context bundle the speech engine and AI see for every meeting in this project.",
  "projects.descriptionLabel": "Description",
  "projects.descriptionOptional": "(optional)",
  "projects.nameRequired":     "Name is required.",

  /* ---- Help ---- */
  "help.heading":           "Help & documentation",
  "help.gettingStarted":    "Getting started",
  "help.contextTips":       "Writing good context",
  "help.exportFormats":     "Export formats",
  "help.keyboardShortcuts": "Keyboard shortcuts",

  /* ---- Admin ---- */
  "admin.heading":          "Admin",
  "admin.users":            "Users",
  "admin.inviteUser":       "Invite user",
  "admin.auditLog":         "Audit log",
  "admin.workspaceSettings":"Workspace settings",

  /* ---- Common actions ---- */
  "common.save":    "Save",
  "common.cancel":  "Cancel",
  "common.delete":  "Delete",
  "common.archive": "Archive",
  "common.restore": "Restore",
  "common.edit":    "Edit",
  "common.close":   "Close",
  "common.loading": "Loading…",
  "common.retry":   "Retry",
  "common.confirm": "Confirm",
  "common.back":    "Back",

  /* ---- Journey strip ---- */
  "journey.stageUpload":        "Upload",
  "journey.stageBriefing":      "Briefing",
  "journey.stageTranscription": "Transcription",
  "journey.stageAnalysis":      "Analysis",
  "journey.stageReview":        "Your review",
  "journey.stageFinal":         "Final",
  "journey.briefingActive":     "Turning your notes into the briefing the transcriber follows…",
  "journey.briefingHint":       "Usually 10–30 seconds",
  "journey.briefingNeedsYou":   "The briefing is ready for your eyes before transcription starts.",
  "journey.briefingReviewCta":  "Review the briefing ↓",
  "journey.transcriptionActive":"Turning the recording into text — exactly what was said, never rewritten…",
  "journey.transcriptionHint":  "Usually 1–3 minutes for short recordings",
  "journey.analysisActive":     "Reading the whole conversation: who said what (double-checked), suspicious wording, names & terms, and a summary…",
  "journey.analysisHint":       "Usually 30–60 seconds — this is what makes your review fast",
  "journey.reviewHeader":       "The analysis found {n} things worth your eyes.",
  "journey.reviewAllClear":     "Everything handled — nothing left to review.",
  "journey.chipSpeakers":       "{n} speaker suggestions",
  "journey.chipMishearings":    "{n} possible mishearings",
  "journey.chipNamesTerms":     "{n} proposed names & terms",
  "journey.chipNamesTermsUnassigned": "{n} names & terms — assign to a project to add them",
  "journey.chipSummary":        "Read the summary",
  "journey.chipSummaryGenerate":"No summary yet — generate one",
  "journey.reworkOfferLead":    "Something the analysis missed?",
  "journey.reworkSection":      "Rework a section…",
  "journey.updateAnalysis":     "Update the whole analysis…",
  "journey.updating":           "Updating…",
  "journey.lockAnyway":         "Lock anyway",
  "journey.lockClean":          "Lock this meeting",
  "journey.lockConfirm":        "{n} items still pending — lock anyway?",
  "journey.lockConfirmYes":     "Yes, lock it",
  "journey.locking":            "Locking…",
  "journey.unlocking":          "Unlocking…",
  "journey.finalLine":          "Final — locked {date} · a finished, trusted source",
  "journey.tryAgain":           "Try again",
  "journey.errorBriefing":      "Preparing the briefing failed.",
  "journey.errorTranscription": "Transcription failed.",
  "journey.errorAnalysis":      "The analysis failed.",
  "journey.mobileLine":         "Stage {x} of 6 — {stage}",
  "journey.mobileLeft":         "{n} left",
  "journey.reworking":          "Reworking {from}–{to}…",
  "journey.integrityBroken":    "The transcription itself looks broken — read why and start over",
  "journey.integrityHeading":   "The analysis thinks the transcription itself is broken",
  "journey.integrityStartOverLead": "Starting over re-transcribes the audio and erases your manual work — the same guarded action as always. The evidence above is the legitimate reason to use it.",
  "journey.exportMenuLabel":    "Export options",
  "journey.lockTitle":          "Mark this meeting as final.",

  /* ---- Rework a section ---- */
  "rework.title":        "Rework this section",
  "rework.body":         "Re-checks who's speaking and flags misheard words in this stretch only. Fresh suggestions land in the review list; your own fixes anywhere are untouched.",
  "rework.hintLabel":    "Anything the analysis should know about this stretch? (optional)",
  "rework.hintPlaceholder": "e.g. 'יואב left the room ~min 35; the one speaking after is אורי'",
  "rework.costEstimate": "Rough cost: ~${cost}",
  "rework.confirm":      "Send for rework",
  "rework.sending":      "Sending…",
  "rework.readout":      "{from} → {to} · {n} paragraphs · ~{min} min of audio",
  "rework.readoutSingle": "{from} → {to} · 1 paragraph · ~{min} min of audio",
  "rework.readoutHint":  "Tap the first and last paragraph of the stretch — then send it for rework.",
  "rework.selectingHeader": "Select the section to rework",
  "rework.selectingSub": "Tap the first and last paragraph of the stretch — then send it for rework.",
  "rework.continue":     "Continue",
  "rework.clear":        "Clear selection",
  "rework.capStart":     "Start",
  "rework.capEnd":       "End",
  "rework.capSingle":    "Section",
  "rework.entry":        "Rework a section",

  /* ---- Common with interpolation ---- */
  "common.nItems":  "{n} items",
  "common.xOfY":    "{x} of {y}",
} as const;

export type StringKey = keyof typeof en;
